import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import { errorHttpReal, sinInterceptorGlobal } from 'src/app/shared/utils/error-http-real';

export interface VesBalanceResponse {
  userId: string;
  balanceVes: number;
  lastMovementDate: string | null;
}

export interface VesMovementItem {
  id: number;
  userId: string;
  type: string;
  creditVes: number;
  debitVes: number;
  balanceVes: number;
  description: string;
  referenceId: string;
  status: string;
  createdAt: string;
}

export interface VesMovementsResponse {
  items: VesMovementItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface VesMovementsQuery {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}

/** Cotización del servidor para comprar bolívares con CLP (chilenos). */
export interface CotizacionCompraVesClp {
  montoClp: number;
  montoVes: number;
  tasa: number;
  montoMinimoClp: number;
  montoMaximoClp: number;
}

export interface CompraVesClpDto {
  montoClp: number;
  /** Tasa que vio el cliente; el servidor rechaza con 409 si cambió. */
  tasaCotizada: number;
  /** Uno por intento de compra: un reintento con el mismo no compra dos veces. */
  clientRequestId: string;
}

export interface CompraVesClpResultado {
  referenceId: string;
  montoClp: number;
  montoVes: number;
  tasa: number;
  balanceVes: number;
}

export interface CompraUsdtDto {
  paisOrigen: string;
  userId: number;
  totalMonedaOrigen: number;
  totalMonedaDestino: number;
  monedaOrigen: string;
  monedaDestino: string;
  paisDestino: string;
  formaPago: string;
  diferencia: number | null;
  tasa: number;
}

export interface RetiroUsdtVesDto {
  walletAddress: string;
  network: 'TRC20' | 'ERC20' | 'BEP20';
  totalMonedaOrigen: number;
  tasa: number;
  fee: number;
}

export interface UsdtCardexItem {
  id: string;
  userId: string;
  txId: string;
  binanceId: string;
  coin: string;
  network: string;
  amount: number;
  description: string | null;
  debe: number;
  haber: number;
  saldo: number;
  createdAt: string;
  /** Solo PAYOUT_USDT_COP: estado de la transacción ligada (POR_PROCESAR/ENVIADA/RECHAZADA). */
  payoutEstado?: string | null;
}

export interface UsdtWithdrawalItem {
  id: string;
  userId: string;
  walletAddress: string;
  network: string;
  coin: string;
  requestedAmount: number;
  fee: number;
  netAmount: number;
  status: string;
  binanceTxId: string | null;
  rejectionReason: string | null;
  requestedAt: string;
  completedAt: string | null;
  createdAt: string;
}

export interface UsdtPendingSwapItem {
  id: number;
  userId: string;
  type: string;
  creditVes: number;
  debitVes: number;
  balanceVes: number;
  description: string;
  referenceId: string;
  status: string;
  createdAt: string;
}

export interface UsdtCardexResponse {
  items: UsdtCardexItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  withdrawals: UsdtWithdrawalItem[];
  pendingSwaps: UsdtPendingSwapItem[];
}

export interface UsdtBalanceResponse {
  userId: string;
  saldo: number;
}

export interface RetiroVesClpDto {
  userId: number;
  totalMonedaOrigen: number;
  totalMonedaDestino: number;
  cuentaBeneficiarioId: number;
  tasa: number;
  tasaCambioId?: number;
  valorCambioId?: number;
}

/**
 * Un tramo de VES→USDT con su tasa ya calculada sobre la base vigente. El
 * cliente no recibe costo, márgenes ni modo: eso es de operadores.
 */
export interface TramoVesUsdt {
  clave: string;
  orden: number;
  /** Cortes en USDT (equivalente a la tasa base). */
  desde: number;
  hasta: number;
  /** VES por USDT de este tramo. Nunca mayor que la base. */
  tasa: number;
  /** Cuánto mejora la base para el cliente, en %. */
  mejoraPct: number;
}

/** GET rates/tramos-usdt/ves-usdt. `tramos` vacío = todos pagan la base. */
export interface TramosVesUsdtResponse {
  base: number | null;
  fechaBase: string | null;
  tramos: TramoVesUsdt[];
  /** Tasa manual vigente de VES→USDT y hasta cuándo rige (aditivo del back). */
  manualVigente?: { tasa: number; hasta: string } | null;
  /**
   * Aditivo (23/10): precio del USDT con que el servidor pasa el monto en VES
   * a USDT para elegir el tramo (`montoVes ÷ precioCorte`). Null o ausente = la
   * base, como antes.
   */
  precioCorte?: number | null;
}

export interface SwapUsdtVesDto {
  userId: number;
  totalUsdt: number;
  totalVes: number;
  tasa: number;
}

export interface SwapUsdtVesResponse {
  success: boolean;
  data: {
    message: string;
    referenceId: string;
    userId: string;
    userName: string;
    usdtMovementId: string;
    vesMovementId: string;
    operacion: {
      usdtDebitado: number;
      vesAcreditado: number;
      tasaAplicada: number;
    };
    balances: {
      usdt: { saldoAnterior: number; nuevoSaldo: number };
      ves: { saldoAnterior: number; nuevoSaldo: number };
    };
  };
}

/**
 * Error de las operaciones del cliente en el wallet VES (`compra-usdt`,
 * `retiro-usdt-ves`, `retiro-ves-clp`, `swap-usdt-ves`), con la misma forma de
 * siempre: `{ status, message, detalles, transitorio }`.
 *
 * Esas llamadas van con `X-Skip-Error-Interceptor`, así que llega el
 * `HttpErrorResponse` real (el 409 de "la tasa cambió", el 403, el 5xx…) con
 * su status. La traducción vive en `shared/utils/error-http-real.ts` y sigue
 * aceptando el string aplanado por si alguna llamada sale sin el escape: ahí
 * se muestra el mensaje del API salvo que sea un `statusText` de protocolo.
 */
export function errorOperacionVesUsdt(
  error: any,
  fallback: string,
): { status: number | undefined; message: string; detalles: any; transitorio: boolean } {
  // Todas mueven dinero: un 5xx es incierto aunque traiga mensaje.
  return errorHttpReal(error, fallback, { conEscape: true, mueveDinero: true });
}

@Injectable({
  providedIn: 'root',
})
export class WalletVesClientService {
  private baseUrl = environment.apiNest;

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session?.accessToken?.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Authorization', `Bearer ${session.accessToken.jwtToken.trim()}`);
  }

  /**
   * Headers de las operaciones que necesitan el error real (status y
   * mensaje): sin pasar por el `ErrorInterceptor` global, que lo aplana.
   */
  private async getHeadersOperacion(): Promise<HttpHeaders> {
    return sinInterceptorGlobal(await this.getHeaders());
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || `Error del servidor: ${error.status}`;
    }
    return throwError(() => errorMessage);
  }

  getMyBalance(): Observable<{ success: boolean; data: VesBalanceResponse }> {
    const url = `${this.baseUrl}wallet-ves-client/my-balance`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: VesBalanceResponse }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  cotizarCompraVesClp(montoClp: number): Observable<{ success: boolean; data: CotizacionCompraVesClp }> {
    const url = `${this.baseUrl}wallet-ves-client/compra-ves-clp/cotizacion`;
    const params = new HttpParams().set('montoClp', String(montoClp));
    return from(this.getHeadersOperacion()).pipe(
      switchMap((headers) =>
        this.http.get<{ success: boolean; data: CotizacionCompraVesClp }>(url, { headers, params }),
      ),
      catchError((error: HttpErrorResponse) =>
        throwError(() => errorHttpReal(error, 'No se pudo cotizar la compra de bolívares.', { conEscape: true })),
      ),
    );
  }

  /** Compra VES con el saldo CLP y los deja en el saldo VES del cliente. */
  compraVesClp(dto: CompraVesClpDto): Observable<{ success: boolean; data: CompraVesClpResultado }> {
    const url = `${this.baseUrl}wallet-ves-client/compra-ves-clp`;
    return from(this.getHeadersOperacion()).pipe(
      switchMap((headers) =>
        this.http.post<{ success: boolean; data: CompraVesClpResultado }>(url, dto, { headers }),
      ),
      catchError((error: HttpErrorResponse) =>
        throwError(() => errorOperacionVesUsdt(error, 'Error procesando la compra de bolívares.')),
      ),
    );
  }

  compraUsdt(dto: CompraUsdtDto): Observable<any> {
    const url = `${this.baseUrl}wallet-ves-client/compra-usdt`;
    return from(this.getHeadersOperacion()).pipe(
      switchMap((headers) => this.http.post<any>(url, dto, { headers })),
      catchError((error: HttpErrorResponse) =>
        throwError(() => errorOperacionVesUsdt(error, 'Error procesando la compra de USDT.')),
      ),
    );
  }

  retiroVes(dto: { userId: number; totalMonedaOrigen: number; cuentaBeneficiarioId: number }): Observable<any> {
    const url = `${this.baseUrl}wallet-ves-client/retiro-ves`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, dto, { headers })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          status: error.status,
          message: error.error?.message || 'Error procesando el retiro de VES.',
          detalles: error.error?.detalles || null,
        }));
      }),
    );
  }

  retiroUsdtVes(dto: RetiroUsdtVesDto): Observable<any> {
    const url = `${this.baseUrl}wallet-ves-client/retiro-usdt-ves`;
    return from(this.getHeadersOperacion()).pipe(
      switchMap((headers) => this.http.post<any>(url, dto, { headers })),
      catchError((error: HttpErrorResponse) =>
        throwError(() => errorOperacionVesUsdt(error, 'Error procesando el retiro de USDT.')),
      ),
    );
  }

  getMyUsdtBalance(): Observable<{ success: boolean; data: UsdtBalanceResponse }> {
    const url = `${this.baseUrl}wallet-ves-client/my-usdt-balance`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: UsdtBalanceResponse }>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getMyUsdtMovements(page: number = 1, limit: number = 20): Observable<{ success: boolean; data: UsdtCardexResponse }> {
    const url = `${this.baseUrl}wallet-ves-client/my-usdt-movements`;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: UsdtCardexResponse }>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  crearReferenciaVes(dto: { userId: number; banco: string; cuenta: string; tipoCuenta: string; titular: string; referencia: string; monto: number }): Observable<any> {
    const url = `${this.baseUrl}wallet-ves-client/referencias-ves`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.post<any>(url, dto, { headers })),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => ({
          status: error.status,
          message: error.error?.message || 'Error creando la referencia VES.',
          detalles: error.error?.detalles || null,
        }));
      }),
    );
  }

  getMyReferenciasVes(page: number = 1, limit: number = 20): Observable<any> {
    const url = `${this.baseUrl}wallet-ves-client/my-referencias-ves`;
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<any>(url, { headers, params })),
      catchError(this.handleError),
    );
  }

  retiroVesClp(dto: RetiroVesClpDto): Observable<any> {
    const url = `${this.baseUrl}wallet-ves-client/retiro-ves-clp`;
    return from(this.getHeadersOperacion()).pipe(
      switchMap((headers) => this.http.post<any>(url, dto, { headers })),
      // Error real (status y mensaje): el 409 de "la tasa cambió" llega como
      // 409 y no como el 401 sintético de los interceptores.
      catchError((error: HttpErrorResponse) =>
        throwError(() => errorOperacionVesUsdt(error, 'Error procesando el retiro VES a CLP.')),
      ),
    );
  }

  swapUsdtToVes(dto: SwapUsdtVesDto): Observable<SwapUsdtVesResponse> {
    const url = `${this.baseUrl}wallet-ves-client/swap-usdt-ves`;
    return from(this.getHeadersOperacion()).pipe(
      switchMap((headers) => this.http.post<SwapUsdtVesResponse>(url, dto, { headers })),
      // Incierto (`transitorio`) sólo sin conexión, timeout o 5xx sin mensaje
      // propio del API: ahí el swap pudo haberse hecho. Un 4xx o un mensaje
      // real del servidor se muestra y se puede reintentar.
      catchError((error: HttpErrorResponse) =>
        throwError(() => errorOperacionVesUsdt(error, 'Error procesando el swap USDT a VES.')),
      ),
    );
  }

  /**
   * Tramos de USDT→VES con la base vigente (21/10/2026, espejo de VES→USDT).
   * Misma forma que `getTramosVesUsdt`; acá un tramo más grande paga MÁS VES
   * por USDT (tasa ≥ base). JSON plano, sin `{ success, data }`.
   */
  getTramosUsdtVes(): Observable<TramosVesUsdtResponse> {
    const url = `${this.baseUrl}rates/tramos-usdt/usdt-ves`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<TramosVesUsdtResponse>(url, { headers })),
      catchError(this.handleError),
    );
  }

  /** Tramos de VES→USDT con la base vigente. JSON plano, sin `{ success, data }`. */
  getTramosVesUsdt(): Observable<TramosVesUsdtResponse> {
    const url = `${this.baseUrl}rates/tramos-usdt/ves-usdt`;
    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<TramosVesUsdtResponse>(url, { headers })),
      catchError(this.handleError),
    );
  }

  getMyMovements(query: VesMovementsQuery = {}): Observable<{ success: boolean; data: VesMovementsResponse }> {
    const url = `${this.baseUrl}wallet-ves-client/my-movements`;
    let params = new HttpParams();
    if (query.page) params = params.set('page', query.page.toString());
    if (query.limit) params = params.set('limit', query.limit.toString());
    if (query.startDate) params = params.set('startDate', query.startDate);
    if (query.endDate) params = params.set('endDate', query.endDate);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => this.http.get<{ success: boolean; data: VesMovementsResponse }>(url, { headers, params })),
      catchError(this.handleError),
    );
  }
}
